import {
  WorkspaceMemberRole,
  type AgentProviderSelectionResponseDto,
  type AgentProviderSelectionUpdateDto,
  type ApiHealthDto,
  type ApiLivenessDto,
  type ArtifactMetadataDto,
  type ArtifactReadUrlDto,
  type CreateProjectGoalParametersDto,
  type CreateProjectGoalResponseDto,
  type CrossValidationReportDto,
  type CrossValidationReportSummaryDto,
  type EstimatorReportComparisonDto,
  type EstimatorReportDto,
  type EstimatorReportSummaryDto,
  type FeaturesDto,
  type InvitationDto,
  type PendingWorkspaceInvitationDto,
  type ListReportsResponseDto,
  type MetricTypeDto,
  type ProjectDto,
  type ProjectGoalMeetingReportsCountDto,
  type ProjectGoalsListDto,
  type ReportSummaryDto,
  type UpdateProjectGoalParametersDto,
  type UserAPIKeyDto,
  type UserDto,
  type WorkspaceDto,
  type WorkspaceInvitationLinkDto,
  type WorkspacePublicIdAvailabilityDto,
} from "@/dto";
// here we need to import the router instance instead of using the composable
// because inject is not available
import { router } from "@/router";
import { RouteNames } from "@/routes";
import {
  apiDelete,
  apiGet,
  apiPost,
  apiPut,
  checkResponseStatus,
  type ApiRequestOptions,
} from "@/services/apiClient";
import { getErrorMessage, sha } from "@/services/utils";
import { useAuthStore } from "@/stores/auth";
import { useToastsStore } from "@/stores/toasts";

interface PaginatedResponse<T> {
  items: T[];
  next_cursor: number | null;
}

export interface ApiCallOptions {
  abortSignal?: AbortSignal;
  /** When false, errors are not shown to the user (no toast / login redirect). Defaults to true. */
  reportErrors?: boolean;
}

export interface ArtifactUploadFields {
  filename?: string;
  description?: string;
  conversationId?: string;
}

export function useApi() {
  const authStore = useAuthStore();

  function _reportError(message: string, statusCode?: number) {
    if (statusCode === 401) {
      router.push({
        name: RouteNames.Login,
        query: { next: router.currentRoute.value.fullPath },
      });
      return;
    }
    console.error(message);
    try {
      const toastsStore = useToastsStore();
      toastsStore.addToast(message, "error", { duration: 10 });
    } catch {}
  }

  function _redirectIfNotAuthenticated() {
    if (!authStore.isAuthenticated) {
      router.push({ name: RouteNames.Logout });
    }
  }

  function _makeOnError(
    options?: ApiCallOptions,
    beforeReport?: (error: unknown, statusCode?: number) => void
  ) {
    return (error: unknown, statusCode?: number) => {
      beforeReport?.(error, statusCode);
      if (options?.reportErrors !== false) {
        _reportError(getErrorMessage(error), statusCode);
      }
    };
  }

  function _apiClientOptions<T = unknown>(
    options?: ApiCallOptions,
    beforeReport?: (error: unknown, statusCode?: number) => void
  ): Pick<ApiRequestOptions<T>, "onError" | "signal"> {
    return {
      ...(options?.abortSignal && { signal: options.abortSignal }),
      onError: _makeOnError(options, beforeReport),
    };
  }

  /**
   * Check a device token against the IdP
   * @param state the state given by the IdP
   * @param user_code the otp
   * @returns true if success false otherwise
   */
  async function deviceCallback(state: string, user_code: string, options?: ApiCallOptions) {
    const result = await apiPost<boolean>(
      `/identity/oauth/device/callback`,
      { state, user_code },
      {
        defaultValue: false,
        ..._apiClientOptions(options),
      }
    );
    return result ?? false;
  }

  /**
   * Fetch a workspace the user has access to.
   * @param workspaceId
   * @returns a workspace dto
   */
  async function fetchWorkspace(workspaceId: number, options?: ApiCallOptions) {
    _redirectIfNotAuthenticated();
    const workspace = await apiGet<WorkspaceDto>(`/identity/workspaces/${workspaceId}`, {
      ..._apiClientOptions(options),
    });
    return workspace ?? null;
  }

  /**
   * Get the list of workspaces the user has access to.
   * @returns a list of workspaces
   */
  async function listWorkspaces(options?: ApiCallOptions) {
    _redirectIfNotAuthenticated();
    let nextCursor: number | null = 0;
    const workspaces = [];
    while (nextCursor !== null) {
      const data: PaginatedResponse<WorkspaceDto> | undefined = await apiGet<
        PaginatedResponse<WorkspaceDto>
      >(`/identity/workspaces/?cursor=${nextCursor}`, {
        ..._apiClientOptions(options, () => {
          nextCursor = null;
        }),
      });
      if (data) {
        workspaces.push(...data.items);
        nextCursor = data.next_cursor;
      }
    }
    return workspaces;
  }

  /**
   * Fetch a workspace by its public id
   * @param workspacePublicId the public id of the workspace
   * @returns a workspace dto
   */
  async function fetchWorkspaceByPublicId(workspacePublicId: string, options?: ApiCallOptions) {
    _redirectIfNotAuthenticated();
    const workspaces = await listWorkspaces(options);
    return workspaces.find((w) => w.public_id === workspacePublicId) ?? null;
  }

  /**
   * Check if a workspace name's generated slug is available and get a suggestion.
   * When available, `suggested_slug` is always null; when taken, it is always a string.
   */
  async function checkworkspacePublicIdAvailability(public_id: string, options?: ApiCallOptions) {
    _redirectIfNotAuthenticated();
    return await apiGet<WorkspacePublicIdAvailabilityDto>(
      `/identity/workspaces/public-id-availability?public_id=${public_id}`,
      {
        ..._apiClientOptions(options),
      }
    );
  }

  /**
   * Create a workspace
   * @param name the workspace name
   * @param ownerId the owner id
   * @returns the newly created workspace or null if something went wrong.
   */
  async function createWorkspace(name: string, ownerId: string, options?: ApiCallOptions) {
    _redirectIfNotAuthenticated();
    const workspace = await apiPost<WorkspaceDto>(
      `/identity/workspaces/`,
      { public_id: name, owner_id: ownerId },
      {
        ..._apiClientOptions(options),
      }
    );
    return workspace?.id ?? null;
  }

  /**
   * Update a workspace
   * @param workspaceId the workspace id
   * @param publicId the workspace public id
   * @returns true if the workspace has been updated false otherwise
   */
  async function updateWorkspace(workspaceId: number, publicId: string, options?: ApiCallOptions) {
    _redirectIfNotAuthenticated();
    const success = await apiPut<boolean>(
      `/identity/workspaces/${workspaceId}`,
      { public_id: publicId },
      {
        expectedStatus: 204,
        defaultValue: true,
        ..._apiClientOptions(options),
      }
    );
    return success ?? false;
  }

  /**
   * Delete a workspace
   * @param workspaceId the workspace id
   * @returns true if the workspace has been deleted false otherwise
   */
  async function deleteWorkspace(workspaceId: number, options?: ApiCallOptions) {
    _redirectIfNotAuthenticated();
    const success = await apiDelete<boolean>(`/identity/workspaces/${workspaceId}`, {
      expectedStatus: 204,
      defaultValue: true,
      ..._apiClientOptions(options),
    });
    return success ?? false;
  }

  /**
   * Invite workspace members
   * @param workspaceId the workspace id
   * @param emails the emails of the members to invite
   * @param role role assigned to invitees (default: contributor)
   * @param options optional API call options
   * @returns true if the invitation has been sent false otherwise
   */
  async function inviteWorkspaceMembers(
    workspaceId: number,
    emails: string[],
    role: WorkspaceMemberRole = WorkspaceMemberRole.Contributor,
    options?: ApiCallOptions
  ) {
    _redirectIfNotAuthenticated();

    const invitations = emails.map((email) =>
      apiPost<InvitationDto>(
        `/identity/workspaces/${workspaceId}/invitations`,
        {
          role,
          email,
        },
        {
          ..._apiClientOptions(options),
        }
      )
    );

    const results = await Promise.all(invitations);
    return results.every((result) => result !== null);
  }

  /**
   * Create a time-limited invitation URL for a workspace (same join flow as email invites).
   * @param workspaceId the workspace id
   * @param role the role granted through the link
   * @param expiresAt ISO datetime after which the link no longer works
   */
  async function createWorkspaceInvitationLink(
    workspaceId: number,
    role: WorkspaceMemberRole,
    expiresAt: string,
    options?: ApiCallOptions
  ) {
    _redirectIfNotAuthenticated();
    return apiPost<WorkspaceInvitationLinkDto>(
      `/identity/workspaces/${workspaceId}/invitation-link`,
      { role, expires_at: expiresAt },
      {
        expectedStatus: 201,
        ..._apiClientOptions(options),
      }
    );
  }

  /**
   * List unused, unexpired workspace invitations (email and shareable links).
   */
  async function listWorkspaceInvitations(workspaceId: number, options?: ApiCallOptions) {
    _redirectIfNotAuthenticated();
    return (
      (await apiGet<PendingWorkspaceInvitationDto[]>(
        `/identity/workspaces/${workspaceId}/invitations`,
        {
          ..._apiClientOptions(options),
        }
      )) ?? []
    );
  }

  /**
   * Revoke a pending workspace invitation.
   */
  async function deleteWorkspaceInvitation(
    workspaceId: number,
    invitationId: number,
    options?: ApiCallOptions
  ) {
    _redirectIfNotAuthenticated();
    const success = await apiDelete<boolean>(
      `/identity/workspaces/${workspaceId}/invitations/${invitationId}`,
      {
        expectedStatus: 204,
        defaultValue: true,
        ..._apiClientOptions(options),
      }
    );
    return success ?? false;
  }

  /**
   * Remove a member from a workspace
   * @param workspaceId the workspace id
   * @param userId the user id
   * @returns true if the member has been removed false otherwise
   */
  async function removeWorkspaceMember(
    workspaceId: number,
    userId: string,
    options?: ApiCallOptions
  ) {
    _redirectIfNotAuthenticated();
    await apiDelete<boolean>(`/identity/workspaces/${workspaceId}/members/${userId}`, {
      ..._apiClientOptions(options),
    });
  }

  /**
   * Update a workspace member's role.
   */
  async function updateWorkspaceMemberRole(
    workspaceId: number,
    userId: string,
    role: WorkspaceMemberRole,
    options?: ApiCallOptions
  ) {
    _redirectIfNotAuthenticated();
    const success = await apiPut<boolean>(
      `/identity/workspaces/${workspaceId}/members/${userId}/role`,
      { role },
      {
        expectedStatus: 204,
        defaultValue: true,
        ..._apiClientOptions(options),
      }
    );
    return success ?? false;
  }

  /**
   * Get the list of project the user has access to.
   * @param workspacePublicId workspace name in which we want to list project
   * @returns a list of projects, or null if the workspace was not found / access denied
   */
  async function listProjects(workspacePublicId: string, options?: ApiCallOptions) {
    const projects = await apiGet<ProjectDto[]>(`/projects/${workspacePublicId}`, {
      ..._apiClientOptions(options),
    });
    return projects ?? null;
  }

  /**
   * Delete project the user has access to.
   * @param workspacePublicId workspace name to which the project belongs
   * @param projectName the project's name
   * @returns a list of project
   * @returns true if delete has been done false otherwise.
   */
  async function deleteProject(
    workspacePublicId: string,
    projectName: string,
    options?: ApiCallOptions
  ) {
    _redirectIfNotAuthenticated();
    const result = await apiDelete<boolean>(`/projects/${workspacePublicId}/${projectName}`, {
      defaultValue: false,
      ..._apiClientOptions(options),
    });
    return result ?? false;
  }

  /**
   * Create an empty project.
   * @param workspacePublicId workspace the project belongs to
   * @param projectName the project's name
   * @returns the created project, a FastAPI error for the caller to interpret, or null on network failure
   */
  async function createProject(
    workspacePublicId: string,
    projectName: string,
    options?: ApiCallOptions
  ) {
    _redirectIfNotAuthenticated();
    const clientOptions = _apiClientOptions(options);
    const result = await apiPost<{ id: number; url: string }>(
      `/projects/${encodeURIComponent(workspacePublicId)}/${encodeURIComponent(projectName)}`,
      undefined,
      {
        returnErrorResponse: true,
        ...clientOptions,
        onError: (error, statusCode, apiError) => {
          if (apiError && statusCode !== 401) {
            return;
          }
          clientOptions.onError?.(error, statusCode);
        },
      }
    );
    return result ?? null;
  }

  /**
   * List merged estimator and cross-validation report summaries for a project.
   * Follows cursor pagination until all pages are fetched.
   */
  async function listReports(
    workspacePublicId: string,
    projectName: string,
    searchParams?: URLSearchParams,
    options?: ApiCallOptions
  ): Promise<ReportSummaryDto[]> {
    const requestParams = new URLSearchParams(searchParams);
    const pageSize = 500;
    requestParams.set("limit", String(pageSize));

    const reports: ReportSummaryDto[] = [];
    let cursor: number | null = null;
    let hasMorePages = true;

    while (hasMorePages) {
      const pageParams = new URLSearchParams(requestParams);
      if (cursor !== null) {
        pageParams.set("cursor", String(cursor));
      }
      const page = await apiGet<ListReportsResponseDto>(
        `/projects/${workspacePublicId}/${projectName}/reports?${pageParams}`,
        {
          defaultValue: { items: [], next_cursor: null },
          ..._apiClientOptions(options),
        }
      );
      if (!page?.items.length) {
        break;
      }
      reports.push(...page.items);
      cursor = page.next_cursor;
      hasMorePages = cursor !== null;
    }

    return reports;
  }

  /**
   * Filter estimator reports
   * @param workspacePublicId workspace name to which the project belongs
   * @param projectName the project's name
   * @param datasetFingerprint the dataset  to filter from
   * @param searchParams the search params to send
   * @returns a list of experiments groups
   */
  async function filterEstimatorReports(
    workspacePublicId: string,
    projectName: string,
    datasetFingerprint: string,
    searchParams: URLSearchParams,
    options?: ApiCallOptions
  ): Promise<EstimatorReportSummaryDto[]> {
    // Copy so callers can reuse their URLSearchParams without accumulating
    // dataset_fingerprint / pagination keys across repeated invocations.
    const requestParams = new URLSearchParams(searchParams);
    requestParams.set("dataset_fingerprint", datasetFingerprint);
    const pageSize = 500;
    requestParams.set("limit", String(pageSize));

    const reports: EstimatorReportSummaryDto[] = [];
    let cursorCreatedAt: string | null = null;
    let cursorId: number | null = null;
    let hasMorePages = true;

    while (hasMorePages) {
      const pageParams = new URLSearchParams(requestParams);
      if (cursorCreatedAt !== null && cursorId !== null) {
        pageParams.set("cursor_created_at", cursorCreatedAt);
        pageParams.set("cursor_id", String(cursorId));
      }
      const page = await apiGet<EstimatorReportSummaryDto[]>(
        `/projects/${workspacePublicId}/${projectName}/estimator-reports/?${pageParams}`,
        {
          defaultValue: [],
          ..._apiClientOptions(options),
        }
      );
      if (!page?.length) {
        break;
      }
      reports.push(...page);
      hasMorePages = page.length === pageSize;
      if (hasMorePages) {
        const lastReport = page[page.length - 1];
        cursorCreatedAt = lastReport.created_at;
        cursorId = lastReport.id;
      }
    }

    return reports;
  }

  /**
   * Fetch estimator report
   * @param workspacePublicId workspace name to which the project belongs
   * @param projectName the project's name
   * @param reportId the report id
   */
  async function fetchEstimatorReport(
    workspacePublicId: string,
    projectName: string,
    reportId: number,
    options?: ApiCallOptions
  ) {
    const report = await apiGet<EstimatorReportDto>(
      `/projects/${workspacePublicId}/${projectName}/estimator-reports/${reportId}`,
      {
        ..._apiClientOptions(options),
      }
    );
    return report ?? null;
  }

  /**
   * Filter cross validation reports
   * @param workspacePublicId workspace name to which the project belongs
   * @param projectName the project's name
   * @param datasetFingerprint the dataset  to filter from
   * @param searchParams the search params to send
   * @returns a list of experiments groups
   */
  async function filterCrossValidationReports(
    workspacePublicId: string,
    projectName: string,
    datasetFingerprint: string,
    searchParams: URLSearchParams,
    options?: ApiCallOptions
  ): Promise<CrossValidationReportSummaryDto[]> {
    // Copy so callers can reuse their URLSearchParams without accumulating
    // dataset_fingerprint across repeated invocations.
    const requestParams = new URLSearchParams(searchParams);
    requestParams.set("dataset_fingerprint", datasetFingerprint);
    const data = await apiGet<CrossValidationReportSummaryDto[]>(
      `/projects/${workspacePublicId}/${projectName}/cross-validation-reports/?${requestParams}`,
      {
        defaultValue: [],
        ..._apiClientOptions(options),
      }
    );
    return data;
  }

  /**
   * Fetch cross validation report
   * @param workspacePublicId workspace name to which the project belongs
   * @param projectName the project's name
   * @param reportId the report id
   */
  async function fetchCrossValidationReport(
    workspacePublicId: string,
    projectName: string,
    reportId: number,
    options?: ApiCallOptions
  ) {
    const report = await apiGet<CrossValidationReportDto>(
      `/projects/${workspacePublicId}/${projectName}/cross-validation-reports/${reportId}`,
      {
        ..._apiClientOptions(options),
      }
    );
    return report ?? null;
  }

  /**
   * Fetch a user by it's id
   * @param userId the if of the user to get
   * @returns the user DTO or null if not found
   */
  async function fetchUser(userId: string, options?: ApiCallOptions) {
    _redirectIfNotAuthenticated();
    const user = await apiGet<UserDto>(`/identity/users/${userId}`, {
      ..._apiClientOptions(options),
    });
    return user ?? null;
  }

  /**
   * Fetch the list of comparisons of a given project.
   * @param workspacePublicId workspace name to which the project belongs
   * @param projectName the project's name
   * @returns a list of comparisons.
   */
  async function listComparisons(
    workspacePublicId: string,
    projectName: string,
    options?: ApiCallOptions
  ) {
    const data = await apiGet<EstimatorReportComparisonDto[]>(
      `/projects/${workspacePublicId}/${projectName}/comparison-reports`,
      {
        defaultValue: [],
        ..._apiClientOptions(options),
      }
    );
    return data ?? [];
  }

  /**
   * Fetch a comparison from a given project.
   * @param workspacePublicId workspace name to which the project belongs
   * @param projectName the project's name
   * @param comparisonId the comparison id
   * @returns a comparison or null if something went wrong.
   */
  async function fetchComparison(
    workspacePublicId: string,
    projectName: string,
    comparisonId: number,
    options?: ApiCallOptions
  ) {
    const comparison = await apiGet<EstimatorReportComparisonDto>(
      `/projects/${workspacePublicId}/${projectName}/comparison-reports/${comparisonId}`,
      {
        ..._apiClientOptions(options),
      }
    );
    return comparison ?? null;
  }

  /**
   * Create a comparison in a given project.
   * @param workspacePublicId workspace name to which the project belongs
   * @param projectName the project's name
   * @param name the comparison's name
   * @param comparedReportsIds the complete list of compared reports ids
   * @param bestTradeOffsIds the list of chosen reports ids
   * @param bestTradeOffsExplanations a dict containing explanation for each choice
   * @returns the newly created comparison or null if something went wrong.
   */
  async function createComparisons(
    workspacePublicId: string,
    projectName: string,
    name: string,
    comparedReportsIds: number[],
    bestTradeOffsIds: number[],
    bestTradeOffsExplanations: Record<number, string>,
    options?: ApiCallOptions
  ) {
    _redirectIfNotAuthenticated();
    const comparison = await apiPost<EstimatorReportComparisonDto>(
      `/projects/${workspacePublicId}/${projectName}/comparison-reports`,
      {
        name,
        compared_reports: comparedReportsIds,
        best_trade_offs: bestTradeOffsIds,
        explanations: bestTradeOffsExplanations,
      },
      {
        ..._apiClientOptions(options),
      }
    );
    return comparison ?? null;
  }

  /**
   * List metric types eligible as goals for a project.
   */
  async function listProjectPotentialGoals(
    workspacePublicId: string,
    projectName: string,
    options?: ApiCallOptions
  ) {
    const metrics = await apiGet<MetricTypeDto[]>(
      `/projects/${workspacePublicId}/${projectName}/potential-goals`,
      {
        defaultValue: [],
        ..._apiClientOptions(options),
      }
    );
    return metrics ?? [];
  }

  /**
   * Count reports on a project that meet all goals.
   */
  async function countProjectGoalMeetingReports(
    workspacePublicId: string,
    projectName: string,
    options?: ApiCallOptions
  ) {
    const count = await apiGet<ProjectGoalMeetingReportsCountDto>(
      `/projects/${workspacePublicId}/${projectName}/goals/meeting-reports/count`,
      {
        defaultValue: { meeting_count: 0, total_count: 0 },
        ..._apiClientOptions(options),
      }
    );
    return count ?? { meeting_count: 0, total_count: 0 };
  }

  /**
   * List goals for a project.
   */
  async function listProjectGoals(
    workspacePublicId: string,
    projectName: string,
    options?: ApiCallOptions
  ) {
    const result = await apiGet<ProjectGoalsListDto>(
      `/projects/${workspacePublicId}/${projectName}/goals`,
      {
        defaultValue: { goals: [], main_scopes: [] },
        ..._apiClientOptions(options),
      }
    );
    return result ?? { goals: [], main_scopes: [] };
  }

  /**
   * Create a project goal for a metric.
   */
  async function createProjectGoal(
    workspacePublicId: string,
    projectName: string,
    parameters: CreateProjectGoalParametersDto,
    options?: ApiCallOptions
  ) {
    _redirectIfNotAuthenticated();
    const result = await apiPost<CreateProjectGoalResponseDto>(
      `/projects/${workspacePublicId}/${projectName}/goals`,
      parameters,
      {
        expectedStatus: 201,
        ..._apiClientOptions(options),
      }
    );
    return result?.id ?? null;
  }

  /**
   * Update a project goal constraint.
   */
  async function updateProjectGoal(
    workspacePublicId: string,
    projectName: string,
    goalId: number,
    parameters: UpdateProjectGoalParametersDto,
    options?: ApiCallOptions
  ) {
    _redirectIfNotAuthenticated();
    const success = await apiPut<boolean>(
      `/projects/${workspacePublicId}/${projectName}/goals/${goalId}`,
      parameters,
      {
        expectedStatus: 204,
        defaultValue: true,
        ..._apiClientOptions(options),
      }
    );
    return success ?? false;
  }

  /**
   * Delete a project goal.
   */
  async function deleteProjectGoal(
    workspacePublicId: string,
    projectName: string,
    goalId: number,
    options?: ApiCallOptions
  ) {
    _redirectIfNotAuthenticated();
    const success = await apiDelete<boolean>(
      `/projects/${workspacePublicId}/${projectName}/goals/${goalId}`,
      {
        expectedStatus: 204,
        defaultValue: true,
        ..._apiClientOptions(options),
      }
    );
    return success ?? false;
  }

  /**
   * Delete a comparison from a given project.
   * @param workspacePublicId workspace name to which the project belongs
   * @param projectName the project's name
   * @param comparisonId the comparison id
   * @returns true if deletion has been done false otherwise.
   */
  async function deleteComparison(
    workspacePublicId: string,
    projectName: string,
    comparisonId: number,
    options?: ApiCallOptions
  ) {
    _redirectIfNotAuthenticated();
    const result = await apiDelete<boolean>(
      `/projects/${workspacePublicId}/${projectName}/comparison-reports/${comparisonId}`,
      {
        defaultValue: false,
        ..._apiClientOptions(options),
      }
    );
    return result ?? false;
  }

  /**
   * Fetch API health status.
   */
  async function fetchLiveness(options?: ApiCallOptions) {
    const liveness = await apiGet<ApiLivenessDto>(`/liveness`, {
      ..._apiClientOptions(options),
    });
    return liveness ?? null;
  }

  /**
   * Fetch API health status.
   */
  async function fetchHealth(options?: ApiCallOptions) {
    const health = await apiGet<ApiHealthDto>(`/health`, {
      ..._apiClientOptions(options),
    });
    return health ?? null;
  }

  /**
   * Fetch the optional server-side features enabled on this deployment.
   */
  async function fetchFeatures(options?: ApiCallOptions) {
    return apiGet<FeaturesDto>(`/features`, {
      ..._apiClientOptions(options),
    });
  }

  async function checksumArtifact(content: string | Blob) {
    const hash = await sha(typeof content === "string" ? content : await content.arrayBuffer());
    return `sha1-${hash}`;
  }

  /**
   * Upload an artifact. `filename` stores the original name and makes
   * `content_type` the artifact media type. `description` and `conversationId`
   * are optional; a conversation id attaches the file to that session.
   */
  async function uploadArtifact(
    workspacePublicId: string,
    projectName: string,
    content_type: string,
    content: string | Blob,
    fields?: ArtifactUploadFields,
    options?: ApiCallOptions
  ) {
    try {
      _redirectIfNotAuthenticated();
      const checksum = await checksumArtifact(content);
      const declaration: {
        checksum: string;
        content_type: string;
        filename?: string;
        description?: string;
        conversation_id?: string;
      } = { checksum, content_type };
      if (fields?.filename) declaration.filename = fields.filename;
      if (fields?.description) declaration.description = fields.description;
      if (fields?.conversationId) declaration.conversation_id = fields.conversationId;

      const data = await apiPost<Array<{ upload_url?: string }>>(
        `/projects/${workspacePublicId}/${projectName}/artifacts/`,
        [declaration],
        {
          ..._apiClientOptions(options),
        }
      );

      if (data && data.length > 0 && data[0]) {
        // artifact does not exist - upload to S3
        const uploadUrl = data[0].upload_url;
        if (uploadUrl) {
          // S3 upload - keep raw fetch as it's external API
          const s3Request = await fetch(uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": content_type },
            body: content,
            ...(options?.abortSignal && { signal: options.abortSignal }),
          });
          checkResponseStatus(s3Request, 200);

          // Complete the upload
          await apiPost(
            `/projects/${workspacePublicId}/${projectName}/artifacts/complete`,
            [{ checksum, etags: {} }],
            {
              expectedStatus: 200,
              ..._apiClientOptions(options),
            }
          );
        }
      }

      return checksum;
    } catch (error) {
      if (options?.reportErrors !== false) {
        _reportError(getErrorMessage(error));
      }
    }
  }

  async function listArtifacts(
    workspacePublicId: string,
    projectName: string,
    conversationId?: string,
    options?: ApiCallOptions
  ) {
    _redirectIfNotAuthenticated();
    const params = new URLSearchParams();
    if (conversationId) params.set("conversation_id", conversationId);
    params.set("status", "uploaded");
    const artifacts = await apiGet<ArtifactMetadataDto[]>(
      `/projects/${workspacePublicId}/${projectName}/artifacts/?${params}`,
      { ..._apiClientOptions(options) }
    );
    return artifacts ?? [];
  }

  async function fetchArtifactReadUrls(
    workspacePublicId: string,
    projectName: string,
    checksums: string[],
    options?: ApiCallOptions
  ) {
    _redirectIfNotAuthenticated();
    if (checksums.length === 0) return [];
    const params = new URLSearchParams();
    for (const checksum of checksums) params.append("artifact_checksum", checksum);
    const reads = await apiGet<ArtifactReadUrlDto[]>(
      `/projects/${workspacePublicId}/${projectName}/artifacts/read?${params}`,
      { ..._apiClientOptions(options) }
    );
    return reads ?? [];
  }

  /**
   * Fetch user's API keys
   * @param userId the id of the user to fetch the API keys for
   * @returns a list of API keys
   */
  async function fetchUserApiKeys(userId: string, options?: ApiCallOptions) {
    _redirectIfNotAuthenticated();
    const apiKeys = await apiGet<UserAPIKeyDto[]>(`/identity/users/${userId}/api-keys`, {
      ..._apiClientOptions(options),
    });
    return apiKeys ?? [];
  }

  /**
   * Create a user API key
   * @param userId the id of the user to create the API key for
   * @param name the name of the API key
   * @param workspaceId workspace the key is scoped to
   * @param permissions fine-grained permission names (e.g. create:project)
   * @param expiresAt ISO-8601 expiration instant (UTC recommended); omit for a key that never expires
   * @returns the newly created API key
   */
  async function createUserApiKey(
    userId: string,
    name: string,
    workspaceId: number,
    permissions: string[],
    expiresAt?: string,
    options?: ApiCallOptions
  ) {
    _redirectIfNotAuthenticated();
    const body: Record<string, unknown> = {
      name,
      workspace_id: workspaceId,
      permissions,
    };
    if (expiresAt !== undefined) {
      body.expires_at = expiresAt;
    }
    const result = await apiPost<{
      api_key_id: number;
      api_key: string;
    }>(`/identity/users/${userId}/api-keys`, body, {
      ..._apiClientOptions(options),
    });
    return result?.api_key ?? null;
  }

  /**
   * Delete a user API key
   * @param userId the id of the user to delete the API key for
   * @param apiKeyId the id of the API key to delete
   */
  async function deleteUserApiKey(userId: string, apiKeyId: number, options?: ApiCallOptions) {
    _redirectIfNotAuthenticated();
    await apiDelete<void>(`/identity/users/${userId}/api-keys/${apiKeyId}`, {
      ..._apiClientOptions(options),
    });
  }

  /**
   * Fetch the agent provider selection for a workspace.
   * @param workspaceId the workspace id
   * @returns the selection and UI metadata, or null on error
   */
  async function fetchAgentProviderSelection(workspaceId: number, options?: ApiCallOptions) {
    _redirectIfNotAuthenticated();
    const response = await apiGet<AgentProviderSelectionResponseDto>(
      `/agent/workspaces/${workspaceId}/provider`,
      { ..._apiClientOptions(options) }
    );
    return response ?? null;
  }

  /**
   * Set the agent provider selection for a workspace.
   * @param workspaceId the workspace id
   * @param payload the provider selection to persist
   * @returns the updated selection and UI metadata, or null on error
   */
  async function setAgentProviderSelection(
    workspaceId: number,
    payload: AgentProviderSelectionUpdateDto,
    options?: ApiCallOptions
  ) {
    _redirectIfNotAuthenticated();
    const response = await apiPut<AgentProviderSelectionResponseDto>(
      `/agent/workspaces/${workspaceId}/provider`,
      payload,
      { ..._apiClientOptions(options) }
    );
    return response ?? null;
  }

  return {
    checksumArtifact,
    checkworkspacePublicIdAvailability,
    countProjectGoalMeetingReports,
    createComparisons,
    createProject,
    createProjectGoal,
    createUserApiKey,
    createWorkspace,
    createWorkspaceInvitationLink,
    deleteComparison,
    deleteProject,
    deleteProjectGoal,
    deleteUserApiKey,
    deleteWorkspace,
    deleteWorkspaceInvitation,
    deviceCallback,
    fetchAgentProviderSelection,
    fetchArtifactReadUrls,
    fetchComparison,
    fetchCrossValidationReport,
    fetchEstimatorReport,
    fetchFeatures,
    fetchHealth,
    fetchLiveness,
    fetchUser,
    fetchUserApiKeys,
    fetchWorkspace,
    fetchWorkspaceByPublicId,
    filterCrossValidationReports,
    filterEstimatorReports,
    inviteWorkspaceMembers,
    listArtifacts,
    listComparisons,
    listProjectGoals,
    listProjectPotentialGoals,
    listProjects,
    listReports,
    listWorkspaces,
    listWorkspaceInvitations,
    removeWorkspaceMember,
    setAgentProviderSelection,
    updateProjectGoal,
    updateWorkspace,
    updateWorkspaceMemberRole,
    uploadArtifact,
  };
}
